/**
 * EmeraldCursor — luxury micro-interaction.
 *
 * Per design_guidelines.json: "Hidden default cursor, replaced by a subtle
 * emerald dot that expands over clickable elements."
 *
 * Implementation notes:
 *  - Activates ONLY on devices with a fine pointer (desktop with mouse).
 *  - Does NOT hide the native cursor entirely — that's an a11y / usability
 *    risk. Instead, we *augment* with a soft emerald aura that tracks
 *    the pointer. The native cursor still works for accessibility tooling,
 *    keyboard-emulated cursors, and screen-share recordings.
 *  - Uses requestAnimationFrame + lerp for buttery 60fps tracking.
 *  - Detects hover on `a, button, [role="button"], input, textarea, [data-cursor="hover"]`.
 */
import React, { useEffect, useRef, useState } from 'react';

export default function EmeraldCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const mouse = useRef({ x: -100, y: -100 });
  const dotPos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Detect fine pointer (mouse). Skip on touch / coarse devices.
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setEnabled(mq.matches);
    const onChange = () => setEnabled(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };

    const onOver = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const hot = t.closest(
        'a, button, [role="button"], input, textarea, select, [data-cursor="hover"]'
      );
      setIsHovering(!!hot);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);

    let raf;
    const tick = () => {
      // Dot follows tightly (lerp 0.6); ring trails (lerp 0.18).
      dotPos.current.x += (mouse.current.x - dotPos.current.x) * 0.6;
      dotPos.current.y += (mouse.current.y - dotPos.current.y) * 0.6;
      ringPos.current.x += (mouse.current.x - ringPos.current.x) * 0.18;
      ringPos.current.y += (mouse.current.y - ringPos.current.y) * 0.18;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dotPos.current.x - 3}px, ${dotPos.current.y - 3}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringPos.current.x - 16}px, ${ringPos.current.y - 16}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      {/* Inner dot — tight tracking, always visible */}
      <div
        ref={dotRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9999] w-1.5 h-1.5 rounded-full bg-emerald-400 transition-[width,height,background-color] duration-200"
        style={{
          opacity: 0.9,
          willChange: 'transform',
        }}
      />
      {/* Outer ring — trails behind, expands on hover */}
      <div
        ref={ringRef}
        aria-hidden
        className={`pointer-events-none fixed top-0 left-0 z-[9998] rounded-full border border-emerald-400/60 transition-[width,height,background-color,border-color] duration-200 ease-out ${
          isHovering ? 'w-12 h-12 bg-emerald-400/10 border-emerald-300' : 'w-8 h-8'
        }`}
        style={{
          willChange: 'transform',
        }}
      />
    </>
  );
}
